<template>
    <div class="analytics-nexus" v-if="isMounted">
        <v-table-nexus
            :is-expand="!!tables[activeTab].expand"
            :isLoader="isLoader"
            :limit="limit"
            :total="total"
            :page="page"
            :data="newData.length ? newData : data"
            :headers="tables[activeTab].headers"
            :displays="tables[activeTab].displays"
            @page="changePage"
        >
            <template v-slot:header>
                <div class="p-4">
                    <ul class="flex items-center tabs">
                        <li v-for="(tab, index) of tabs"
                            :key="index"
                            class="tabs__tab"
                            :class="{
                                'active': activeTab === tab.event,
                                'disabled': activeTab === tab.event
                            }"
                            @click="changeTab(tab.event)"
                        >
                            {{$t(tab.name)}}
                        </li>
                    </ul>
                </div>

                <div class="p-4 flex items-center">
                    <div class="flex items-center" ref="actions">
                        <v-limit
                            class="mr-2"
                            :limits="limits"
                            :total="total"
                            :limit="limit"
                            :page="page"
                            @limit="changeLimit"
                        />
                    </div>

                    <div class="input__filter">
                        <v-input-chip-filter
                            :offset="inputOffset"
                            :data="inputs"
                        >
                            <vs-button
                                color="primary"
                                type="filled"
                                size="small"
                                v-bind="$iconData('icon-filter', 'fi-rr-filter')"
                                @click="isFilter = !isFilter"
                            ></vs-button>
                        </v-input-chip-filter>
                    </div>
                </div>
            </template>

            <template v-slot:teams="item">
                <div class="flex items-center">
                    <vs-icon v-bind="$iconData('icon-users', 'fi-rr-users')"/>
                    <div class="ml-2">{{item.name || item.id}}</div>
                </div>
            </template>

            <template v-for="key of tables.advertiserGeoConversion.geoKeys"
                      v-slot:[key]="item"
            >
                <div class="product-name font-medium truncate">{{getDataConversion(item, key)}}</div>
            </template>

            <template v-slot:expand="item">
                <v-table-nexus
                    :is-expand="!!tables[activeTab].expand.expand"
                    :is-parent-expand="!(item.domains || []).length"
                    :is-pagination="false"
                    :limit="(item.affiliates || item.domains || []).length"
                    :total="(item.affiliates || item.domains || []).length"
                    :page="1"
                    :data="(item.affiliates || item.domains || [])"
                    :headers="tables[activeTab].expand.headers"
                    :displays="tables[activeTab].expand.displays"
                >
                    <template v-slot:expand="item">
                        <v-table-nexus
                            v-if="(item.advertisers || []).length"
                            is-parent-expand
                            :is-pagination="false"
                            :limit="(item.advertisers || []).length"
                            :total="(item.advertisers || []).length"
                            :page="1"
                            :data="(item.advertisers || [])"
                            :headers="tables[activeTab].expand.expand.headers"
                            :displays="tables[activeTab].expand.expand.displays"
                        ></v-table-nexus>
                    </template>
                </v-table-nexus>
            </template>
        </v-table-nexus>

        <v-sidebar
            ref="sidebar"
            class="leads-nexus-sidebar"
            width="350px"
            :active="isFilter"
            @close="closeSidebar"
        >
            <template v-slot:header>
                <div class="analytics-nexus-sidebar__container">
                    <div>{{ $t('global.Filters') }}</div>

                    <div class="analytics-nexus-sidebar__actions">
                        <vs-button
                            class="mr-2"
                            radius
                            type="flat"
                            v-bind="$iconData('icon-rotate-cw', 'fi-rr-rotate-right')"
                            @click="resetFilter"
                        ></vs-button>

                        <vs-button
                            class="mr-2"
                            radius
                            type="flat"
                            v-bind="$iconData('icon-x', 'fi-rr-cross-small')"
                            @click="closeSidebar"
                        ></vs-button>
                    </div>
                </div>
            </template>

            <ds-filter
                ssr
                ref="filter"
                is-watch
                is-return-full-data
                :is-actions="false"
                :fields="fields"
                :data="filterOptions"
                :select-data="filterSelectedData"
                @submit="submitFilter"
                @resetFilter="refreshFilter"
                @select="resultFilter"
            >
                <vs-col
                    vs-w="12"
                    vs-sm="12"
                    vs-lg="12"
                    vs-xs="12"
                >
                    <div class="m-2 flex items-center">
                        <vs-radio v-model="direction" vs-name="send_date" vs-value="send_date">{{ $t('date.send_date') }}</vs-radio>
                    </div>
                    <div class="m-2 flex items-center">
                        <vs-radio v-model="direction" vs-name="created_at" vs-value="created_at">{{ $t('date.created_at') }}</vs-radio>
                    </div>
                </vs-col>

                <vs-col
                    vs-w="12"
                    vs-sm="12"
                    vs-lg="12"
                    vs-xs="12"
                >
                    <div class="m-2">
                        <label class="text-sm opacity-75">{{ $t(`date.${direction}`) }}</label>
                        <date-picker
                            @select="selectedDate"
                            :date="date[direction]"
                            :direction="direction"
                        />
                    </div>
                </vs-col>
            </ds-filter>

            <template v-slot:footer>
                <vs-button
                    class="w-full"
                    color="primary"
                    type="filled"
                    @click="filterClick"
                >{{ $t('buttons.Filter') }}</vs-button>
            </template>
        </v-sidebar>
    </div>
</template>

<script>
import ErrorHandler from "@/utils/errorHandler"
import send from "@/utils/fetch"
import {dateParse} from '@/utils/mixins'
import history from '@/mixins/history'

import VTableNexus from "./components/VTableNexus.vue"
import VView from "@/components/VView.vue"
import VLimit from "@/components/VLimit.vue"
import VInputChipFilter from "@/views/analytics/components/VInputChipFilter.vue"
import VSidebar from "@/components/Sidebar.vue"

export default {
    mixins: [dateParse, history],

    data() {
        return {
            isMounted: false,
            isFilter: false,
            isLoader: false,
            inputOffset: 0,
            inputs: [],
            activeTab: '',
            tabs: [
                {name: 'statistic.CvStatistic', event: 'teamsConversion'},
                {name: 'statistic.Statuses_Statistic', event: 'statusesStatistic'},
                {name: 'statistic.advertiser_geoconversion', event: 'advertiserGeoConversion'},
            ],
            limits: [50, 100, 500],
            newData: [],
            data: [],
            page: 1,
            total: 10,
            limit: 50,
            filter: {},
            filterSelectedData: {},
            filterOptions: {},
            direction: 'send_date',
            date: {
                created_at: {startDate: new Date(), endDate: new Date()},
                send_date: {startDate: new Date(), endDate: new Date()},
                first_action_date: {startDate: new Date(), endDate: new Date()},
            },
            selectDateSystem: {
                start: this.dateParse(new Date()),
                end: this.dateParse(new Date()),
                direction: 'send_date'
            },
            selectDate: {
                start: this.dateParse(new Date()),
                end: this.dateParse(new Date()),
                direction: 'send_date'
            },
            fields: [],
            tables: {
                teamsConversion: {
                    headers: [
                        {name: 'global.Teams', key: 'name', value: 'teams', translate: true, display: true, width: '250px'},
                        {name: 'global.Leads', key: 'leads', value: 'total', translate: true, display: true},
                        {name: 'global.Deposits', key: 'deposits', value: 'deposits', translate: true, display: true},
                        {name: 'global.Conversion', key: 'conversion', value: 'conversion', translate: true, display: true},
                    ],
                    displays: {},
                    expand: {
                        headers: [
                            {name: 'global.Affiliates', key: 'affiliate_name', value: 'affiliate_name', translate: true, display: true, width: '250px'},
                            {name: 'global.Leads', key: 'leads', value: 'total', translate: true, display: true},
                            {name: 'global.Deposits', key: 'deposits', value: 'deposits', translate: true, display: true},
                            {name: 'global.Conversion', key: 'conversion', value: 'conversion', translate: true, display: true}
                        ],
                        displays: {},
                    }
                },

                advertiserGeoConversion: {
                    headers: [],
                    displays: {},
                    geoKeys: [],
                },

                statusesStatistic: {
                    headers: [
                        {name: 'global.Status', key: 'name', value: 'name', translate: true, display: true, width: '250px'},
                        {name: 'global.Leads', key: 'leads_total', value: 'leads_total', translate: true, display: true, width: '100px'},
                        {name: 'global.Conversion', key: 'conversion', value: 'conversion', translate: true, display: true},
                    ],
                    displays: {},
                    expand: {
                        headers: [
                            {name: 'global.Domains', key: 'name', value: 'name', translate: true, display: true, width: '250px'},
                            {name: 'global.Leads', key: 'domain_leads_total', value: 'domain_leads_total', translate: true, display: true, width: '100px'},
                            {name: 'global.Total', key: 'amount_leads_by_domain', value: 'amount_leads_by_domain', translate: true, display: true},
                            {name: 'global.Conversion', key: 'conversion', value: 'conversion', translate: true, display: true}
                        ],
                        displays: {},
                        expand: {
                            headers: [
                                {name: 'global.Advertisers', key: 'name', value: 'name', translate: true, display: true, width: '250px'},
                                {name: 'global.Leads', key: 'advertiser_leads_total', value: 'advertiser_leads_total', translate: true, display: true, width: '100px'},
                                {name: 'global.Total', key: 'amount_leads_by_advertiser', value: 'amount_leads_by_advertiser', translate: true, display: true},
                                {name: 'global.Conversion', key: 'conversion', value: 'conversion', translate: true, display: true}
                            ],
                            displays: {},
                        }
                    }
                }
            }
        }
    },

    components: {
        VSidebar,
        VInputChipFilter,
        VLimit,
        VView,
        VTableNexus
    },

    watch: {
        direction(direction) {
            this.resetDate()

            this.selectedDate({
                ...this.date[direction],
                direction: direction
            })
        }
    },

    methods: {
        createInputFilter(date, data) {
            try {
                const dateFilter = [
                    {name: date.start + ' - ' + date.end, noTranslate: true},
                    {name: `date.${date.direction}`}
                ]

                const filters = Object.entries(data).map(([key, value]) => {
                    const field = this.fields.find(item => (item.reKey || item.field) === key)
                    if (field && field.name) {
                        return {
                            name: field.name,
                            total: value.length
                        }
                    }
                })

                this.inputs = [...dateFilter, ...filters]
            } catch (e) {
                new ErrorHandler(e).send
            }
        },

        resetDate(direction) {
            Object.keys(this.date).map(key => {
                if (direction !== key) {
                    this.date[key].startDate = new Date()
                    this.date[key].endDate = new Date()
                }
            })
        },

        selectedDate(date) {
            this.resetDate('created_at')
            this.resetDate('send_date')

            this.date[date.direction].startDate = date.startDate
            this.date[date.direction].endDate = date.endDate

            this.selectDateSystem = {
                direction: date.direction,
                start: this.dateParse(date.startDate),
                end: this.dateParse(date.endDate)
            }
        },

        resultFilter(data) {
            const template = {
                advertisers: 'integrations',
                teams: 'affiliates'
            }

            Object.keys(template).forEach(key => {
                if (data[key]) {
                    const filteredItems = this.filterOptions[key]
                        .filter(item => data[key].includes(item.id))
                        .map(item => ({
                            ...item,
                            [template[key]]: this.objectMutation(item[template[key]])
                        }))

                    const itemsToUpdate = filteredItems.reduce((acc, item) => {
                        acc.push(...item[template[key]])
                        return acc
                    }, [])

                    this.$set(this.filterOptions, template[key], itemsToUpdate)
                } else {
                    this.$delete(this.filterOptions, template[key])
                    if (this.$refs.filter) {
                        delete this.$refs.filter.selected[template[key]]
                    }
                }
            })


            this.$set(this, 'fields', this.createFilterFields(this.filterOptions))
        },

        resetFilter() {
            if (this.$refs.filter) {
                this.$refs.filter.refreshFilter()
            }
        },

        closeSidebar() {
            this.isFilter = false
        },

        copyData(data) {
            return JSON.parse(JSON.stringify(data))
        },

        getIdsOrNames(data) {
            return Object.entries(data).reduce((acc, [key, value]) => {
                acc.ids[key] = value.map(item => item.id)
                acc.names[key] = value.map(item => item.name)

                return acc
            }, {ids: {}, names: {}})
        },

        async submitFilter(data) {
            let transform = {}
            const idsFields = ['teams', 'advertisers', 'integrations_id']
            const filters = this.getIdsOrNames(this.copyData(data))

            this.filter = Object.entries(filters).reduce((acc, [_key, value]) => {
                Object.keys(value).forEach(key => {
                    if (this.activeTab === 'teamsConversion') {
                        transform.integrations = 'integrations_id'
                    } else {
                        transform = {}
                    }

                    const transformedKey = transform[key] || key
                    const shouldInclude = idsFields.includes(transformedKey)

                    if (shouldInclude) {
                        acc[transformedKey] = filters.ids[key]
                    } else {
                        acc[transformedKey] = filters.names[key]
                    }
                })

                return acc
            }, {})

            this.selectDate = this.selectDateSystem

            const transformValue = value => /^[-]$/.test(value) ? null : value

            Object.keys(this.filter).forEach(key => {
                this.filter[key] = Array.isArray(this.filter[key])
                    ? this.filter[key].map(transformValue)
                    : [transformValue(this.filter[key])]
            })

            await this.get({
                event: this.activeTab
            })

            this.closeSidebar()
        },

        async refreshFilter() {
            this.resetDate()
            this.selectedDate({
                ...this.date.send_date,
                direction: 'send_date'
            });

            this.selectDate = this.selectDateSystem
            this.filter = {}

            await this.get({
                event: this.activeTab
            })
        },

        filterClick() {
            if (this.$refs.filter) {
                this.$refs.filter.submitFilter()
            }
        },

        getDataConversion(data, key) {
            const items = (data.conversion_by_geo || []).reduce((acc, data) => {
                Object.assign(acc, data)
                return acc
            }, {})

            return items[key]
        },

        splitDate() {
            const index = (this.page - 1) * this.limit
            return this.data.slice(index, index + this.limit)
        },

        changeLimit(limit) {
            this.limit = limit

            this.newData = this.splitDate()
        },

        changePage(page) {
            this.page = page

            this.newData = this.splitDate()
        },

        createDisplayFields(tableOptions) {
            return tableOptions.headers.reduce((res, data) => {
                res[data.value] = tableOptions.displays[data.value] ? {
                    ...tableOptions.displays[data.value],
                    name: data.name
                } : {name: data.name, value: true}

                return res
            }, {})
        },

        objectMutation(object) {
            return Object.entries(object).map(([id, name]) => ({id: +id, name}))
        },

        async changeTab(event) {
            await this.toHistory({
                'event-tab': event
            })

            await this.get({event})
        },

        createFilterFields(data) {
            const fieldsOrder = [
                'teams',
                'affiliates',
                'advertisers',
                'integrations',
                'traffic_models',
                'geos',
                'offers',
                'domains',
                'countries'
            ]

            return fieldsOrder.reduce((acc, name) => {
                if (data[name]) {
                    acc.push({
                        name: `server_filter.${name}`,
                        type: 'select',
                        label: name,
                        field: name,
                        multiple: true,
                        full: true
                    })
                }

                return acc
            }, [])
        },

        async getFilters(data) {
            const response = await send(this.$url.statistic.main.filters, 'POST', {data}, false)
                .then(response => response.data.filters)

            if (response.affiliates && !response.affiliates.length) {
                delete response.affiliates
            }

            this.fields = this.createFilterFields(response)

            response.offers = this.objectMutation(response.offers)
            response.traffic_models = this.objectMutation(response.traffic_models)

            return response
        },

        async getAdvertiserGeoConversion(data) {
            const response =  await send(this.$url.statistic.main['adv-geo-conversion'], 'POST', {data}, false)
                .then(response => response.data.geo_conversion_by_advertiser)
                .then(response => {
                    const headerKeys = response.reduce((acc, item) => {
                        const items = item.conversion_by_geo.reduce((res, geo) => {
                            res.push(...Object.keys(geo))
                            return res
                        }, [])

                        acc.push(...items)

                        return [...new Set(acc)]
                    }, [])

                    this.tables.advertiserGeoConversion.headers = [
                        {
                            name: 'global.Advertiser',
                            key: 'name',
                            value: 'name',
                            translate: true,
                            display: true,
                            width: '250px'
                        },
                        ...headerKeys.map(name => ({
                            name,
                            key: name,
                            value: name,
                            translate: false,
                            display: true,
                            width: '250px'
                        }))
                    ]

                    this.tables.advertiserGeoConversion.geoKeys = headerKeys

                    return response
                })
                .catch(e => {
                    new ErrorHandler(e).send
                })

            return response
        },

        async getTeamsConversion(data) {
            const response = await send(this.$url.statistic.main.affiliates, 'POST', {data})
                .then(response => response.data.result.teams)
                .then(response => {
                    return response.reduce((res, item) => {
                        const counts = {
                            total: 0,
                            deposits: 0,
                            conversion: 0,
                        }

                        item.affiliates.map(aff =>
                            Object.keys(counts).map(key => counts[key] += aff[key])
                        )

                        res.push({
                            ...item,
                            ...counts
                        })

                        return res.map(item => {
                            item.conversion = (item.deposits / item.total * 100).toFixed(2)
                            return item
                        })
                    }, [])
                })

            return response
        },

        async getStatusesStatistic(data) {
            const response = await send(this.$url.statistic.main.statuses_domain_adv_deps, 'POST', {data}, false)
                .then(response => response.data.statuses_domain_adv)

            return response
        },

        idToName(params) {
            return Object.entries(params).reduce((acc, [key, value]) => {
                acc[key]  = this.filterOptions[key]
                    .filter(item => value.includes(+item.id))
                    .map(item => item.name.toString())

                return acc
            }, {})
        },

        async get(options) {
            try {
                this.isLoader = true

                const params = {
                    ...this.selectDate,
                    ...this.filter,
                }

                const methods = {
                    teamsConversion: 'getTeamsConversion',
                    statusesStatistic: 'getStatusesStatistic',
                    advertiserGeoConversion: 'getAdvertiserGeoConversion',
                }

                const data = await Promise.all([
                    this.getFilters(params),
                    this[methods[options.event]](params)
                ])

                this.filterOptions = data[0]
                this.data = data[1]

                this.tables[options.event].displays = this.createDisplayFields(this.tables[options.event])

                if (this.tables[options.event].expand) {
                    this.tables[options.event].expand.displays = this.createDisplayFields(this.tables[options.event].expand)
                }

                if (this.tables[options.event].expand && this.tables[options.event].expand.expand) {
                    this.tables[options.event].expand.expand.displays = this.createDisplayFields(this.tables[options.event].expand.expand)
                }

                this.activeTab = options.event

                this.total = this.data.length

                this.resultFilter(this.filter)
                this.createInputFilter(this.selectDate, this.filter)
            } catch (e) {
                this.filter = {}
                new ErrorHandler(e).send
            } finally {
                this.isLoader = false
            }
        },

        async historyData() {
            const history = this.getHistory()

            if (history['event-tab']) {
                this.activeTab = history['event-tab']
            } else {
                await this.toHistory({
                    'event-tab': this.tabs[0].event
                })

                this.activeTab = this.tabs[0].event
            }
        }
    },

    async mounted() {
        await this.historyData()

        await this.get({
            event: this.activeTab
        }, false)

        this.$nextTick(() => {
            if (this.$refs.actions) {
                this.inputOffset = this.$refs.actions.clientWidth
            }
        })

        window.onpopstate = async (event) => {
            await this.historyData()

            await this.get({
                event: this.activeTab
            })
        }

        this.isMounted = true
    }
}
</script>

<style lang="scss">
.analytics-nexus {
    &-sidebar {
        &__container {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        &__actions {
            display: flex;
        }
    }
}

.mx-datepicker-popup {
    z-index: 999999 !important;
}
</style>
